import React, { useState } from "react";
import { db, auth } from "../firebase";
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  setDoc,
  arrayUnion
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import styles from "../styles/CreateClub.module.css";

export default function CreateGroup() {
  const [groupName, setGroupName] = useState("");
  const [description, setDescription] = useState("");
  const [privacy, setPrivacy] = useState("public");
  const [category, setCategory] = useState("academic");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const user = auth.currentUser;

  const categories = [
    { value: "academic", label: "📚 Academic" },
    { value: "coding", label: "💻 Coding Club" },
    { value: "cultural", label: "🎭 Cultural" },
    { value: "sports", label: "⚽ Sports" },
    { value: "technical", label: "🔧 Technical" },
    { value: "hobby", label: "🎨 Hobby" },
    { value: "study", label: "📖 Study Group" },
    { value: "project", label: "🔬 Project Team" }
  ];

  const handleCreate = async () => {
    if (!groupName.trim()) {
      alert("Group name is required");
      return;
    }
    if (!user?.email) {
      alert("Please login first");
      return;
    }

    setLoading(true);
    try {
      // Create club document
      const clubRef = await addDoc(collection(db, "clubs"), {
        name: groupName.trim(),
        description: description.trim(),
        privacy: privacy,
        category: category,
        createdBy: {
          uid: user.uid,
          email: user.email,
          name: user.displayName || user.email.split('@')[0]
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        members: [user.uid],
        memberCount: 1,
        posts: 0,
        isActive: true
      });

      // Add user as admin
      await setDoc(doc(db, "club_members", `${clubRef.id}_${user.uid}`), {
        clubId: clubRef.id,
        userId: user.uid,
        userEmail: user.email,
        role: "admin", // admin, moderator, member
        joinedAt: serverTimestamp(),
        status: "active"
      });

      // Update user's clubs
      await setDoc(doc(db, "users", user.uid), {
        clubs: arrayUnion(clubRef.id)
      }, { merge: true });

      alert(`Club "${groupName}" created successfully!`);
      navigate(`/club/${clubRef.id}`);
    } catch (err) {
      console.error("Error creating club:", err);
      alert("Failed to create club: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Create New Club</h2>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          Club Name *
        </label>
        <input
          type="text"
          placeholder="e.g., Coding Club 2024"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          className={styles.input}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          Description
        </label>
        <textarea
          placeholder="Describe your club's purpose, activities, etc."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows="4"
          className={styles.textarea}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          Category
        </label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className={styles.select}
        >
          {categories.map((cat) => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          Privacy
        </label>
        <div className={styles.radioGroup}>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              value="public"
              checked={privacy === "public"}
              onChange={(e) => setPrivacy(e.target.value)}
              className={styles.radioInput}
            />
            🌐 Public (Anyone can join)
          </label>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              value="private"
              checked={privacy === "private"}
              onChange={(e) => setPrivacy(e.target.value)}
              className={styles.radioInput}
            />
            🔒 Private (Invite only)
          </label>
        </div>
      </div>

      <button
        onClick={handleCreate}
        disabled={loading || !groupName.trim()}
        className={styles.submitBtn}
      >
        {loading ? "Creating..." : "Create Club"}
      </button>

      <p className={styles.infoText}>
        * Clubs are official groups for specific interests.
        You'll be the admin and can add moderators.
      </p>
    </div>
  );
}